///
/// EarthBrowser
///
/// Created by Matt Giger on 10/16/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Render-to-texture pass that converts a tile's packed elevation texture
/// into a LINEAR-filtered RGBA8 relief map: tangent-space normal in rg and
/// sky-view occlusion in b (see terrain_normal.glsl). Runs synchronously
/// from the elevation load callback (and again for every cached tile when
/// the exaggeration setting changes), outside the frame loop, so it saves
/// and restores the GL state it touches.
///

import { type vec2 } from "@stovokor/math/vector"
import { FrameBuffer } from "@stovokor/shader/FrameBuffer"
import { ComponentType, DrawMode, InternalFormat, PixelFormat, TextureFilter } from "@stovokor/shader/GLConstants"
import { type ShaderProgram } from "@stovokor/shader/ShaderProgram"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { Texture } from "@stovokor/shader/Texture"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"

// Encoded flat, fully open ground: normal (0, 0, 1) with xy = 0 mapping to
// 0.5, and full sky visibility
const cFlatRelief = new Uint8Array([128, 128, 255, 255])

export class TerrainNormalBaker {
	private context: StovokorContext
	private shader: ShaderProgram
	private framebuffer: FrameBuffer
	private position: VertexAttribute
	private vertexBuffer: VertexBuffer

	constructor(context: StovokorContext) {
		this.context = context
		this.shader = context.getCachedShader(new ShaderSourceDefinition("terrain_normal"))
		this.framebuffer = new FrameBuffer(context)
		this.position = new VertexAttribute(context, 2, [-1, -1, 3, -1, -1, 3])
		this.vertexBuffer = new VertexBuffer(context, [this.position])
	}

	public destroy(): void {
		this.framebuffer.destroy()
		this.vertexBuffer.destroy()
	}

	/**
	 * A 1x1 flat relief map, ready to sample. bake() grows it to the
	 * elevation texture's size in place, so references held by child tiles
	 * (as their parent relief) see the baked result.
	 */
	public createTarget(): Texture {
		const texture = new Texture(this.context, 1, 1,
			InternalFormat.RGBA8, PixelFormat.RGBA, ComponentType.UnsignedByte,
			TextureFilter.Linear, TextureFilter.Linear)
		texture.uploadRawData(cFlatRelief, 1, 1, InternalFormat.RGBA8, PixelFormat.RGBA, ComponentType.UnsignedByte)
		return texture
	}

	/**
	 * Render the relief map for `elevation` into `target` at the given
	 * vertical exaggeration, resizing the target to match. Returns false
	 * (leaving the target as it was) if the shader or source texture isn't
	 * usable.
	 */
	public bake(elevation: Texture, origin: vec2, delta: vec2, exaggeration: number, target: Texture): boolean {
		if(!this.shader.available || !elevation.available || !elevation.textureId || !target.textureId) {
			return false
		}

		const width = elevation.width
		const height = elevation.height
		if(target.width !== width || target.height !== height) {
			target.initialize(width, height)
		}

		// Save/restore through the engine's state cache: no getParameter
		// round trip, and the cache stays exact for the next frame
		const glState = this.context.glState
		const viewport = glState.getViewport()

		try {
			this.framebuffer.attach(WebGL2RenderingContext.COLOR_ATTACHMENT0, target)
			this.framebuffer.bind()
		} catch(error) {
			this.framebuffer.unbind()
			this.context.logger.warn("TerrainNormalBaker: framebuffer incomplete", error)
			return false
		}
		glState.viewport(0, 0, width, height)
		glState.disable(WebGL2RenderingContext.DEPTH_TEST)
		glState.disable(WebGL2RenderingContext.BLEND)

		this.shader.bindAction()()
		this.shader.setupAttributeActionsFrom({ position: this.position }).forEach(action => action())
		this.shader.uniforms.elevationTexture?.bindingFunction(elevation)()
		this.shader.uniforms.origin?.bindingFunction(origin)()
		this.shader.uniforms.delta?.bindingFunction(delta)()
		this.shader.uniforms.elevationMultiplier?.bindingFunction(exaggeration)()
		this.vertexBuffer.draw(DrawMode.Triangles)
		this.shader.disableAttributeActions().forEach(action => action())
		this.shader.unbindAction()()

		this.framebuffer.unbind()
		glState.enable(WebGL2RenderingContext.DEPTH_TEST)
		glState.enable(WebGL2RenderingContext.BLEND)
		glState.viewport(viewport[0], viewport[1], viewport[2], viewport[3])

		target.available = true
		return true
	}
}
